import React, { useState, useMemo } from "react";
import { CALCS, getCalc } from "./tools/calculators";
import { Sprout, ArrowRight, ArrowLeft, Calculator } from "lucide-react";
import { useSEO } from "../lib/useSEO";

function Nav({ onHome, onGetStarted }) {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-forest-900/85 border-b border-white/10">
      <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
        <button data-testid="tools-home" onClick={onHome} className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-5 w-5" strokeWidth={2.4} /></div>
          <span className="font-heading font-extrabold text-xl">Assort<span className="text-ai">IQ</span></span>
        </button>
        <div className="flex items-center gap-4">
          <a href="/tools" className="text-sm font-semibold text-white/70 hover:text-white">Free tools</a>
          <a href="/blog" className="text-sm font-semibold text-white/70 hover:text-white">Resources</a>
          <button data-testid="tools-nav-cta" onClick={onGetStarted} className="group flex items-center gap-1.5 rounded-full bg-ai px-4 py-2 text-sm font-bold hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
            Start free trial <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </header>
  );
}

const VC = { emerald: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10", amber: "text-amber-400 border-amber-400/30 bg-amber-400/10", rose: "text-rose-400 border-rose-400/30 bg-rose-400/10" };

function ExplainerBlock({ b }) {
  if (b.t === "h2") return <h2 className="mt-8 mb-2 font-heading text-xl font-extrabold text-white">{b.c}</h2>;
  if (b.t === "p") return <p className="mt-3 text-white/70 leading-relaxed">{b.c}</p>;
  if (b.t === "ul") return <ul className="mt-3 space-y-2">{b.c.map((li, i) => <li key={i} className="flex gap-2 text-white/70"><span className="text-ai mt-1">•</span><span>{li}</span></li>)}</ul>;
  return null;
}

function CalcTool({ calc, onHome, onGetStarted }) {
  useSEO({
    title: `${calc.title} — Free | AssortIQ`,
    description: calc.description,
    canonical: `https://getassortiq.com/tools/${calc.slug}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: calc.title,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description: calc.description,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      url: `https://getassortiq.com/tools/${calc.slug}`,
    },
  });
  const [vals, setVals] = useState(() => Object.fromEntries(calc.inputs.map((i) => [i.key, i.default])));
  const result = useMemo(() => calc.compute(vals), [calc, vals]);
  const set = (k, val) => setVals((s) => ({ ...s, [k]: val === "" ? 0 : Number(val) }));

  return (
    <div className="min-h-screen bg-forest-900 text-white font-body antialiased">
      <Nav onHome={onHome} onGetStarted={onGetStarted} />
      <div className="mx-auto max-w-4xl px-6 py-12">
        <a href="/tools" data-testid="tools-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" /> All free tools</a>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider">{calc.category}</div>
        <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold leading-tight">{calc.h1}</h1>
        <p className="mt-3 text-white/60 leading-relaxed max-w-2xl">{calc.intro}</p>

        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 space-y-4" data-testid="calc-inputs">
            <div className="text-sm font-bold uppercase tracking-wider text-white/50">Your numbers</div>
            {calc.inputs.map((i) => (
              <label key={i.key} className="block">
                <span className="text-sm text-white/70">{i.label}</span>
                <div className="mt-1 flex items-center rounded-lg border border-white/15 bg-forest-900/60 focus-within:border-ai/60 overflow-hidden">
                  {i.prefix && <span className="pl-3 text-white/40 text-sm">{i.prefix}</span>}
                  <input
                    type="number" data-testid={`calc-input-${i.key}`}
                    value={vals[i.key]} onChange={(e) => set(i.key, e.target.value)}
                    className="w-full bg-transparent px-3 py-2.5 text-white outline-none" />
                  {i.suffix && <span className="pr-3 text-white/40 text-sm whitespace-nowrap">{i.suffix}</span>}
                </div>
              </label>
            ))}
          </div>

          <div className="rounded-2xl border border-ai/25 bg-ai/[0.05] p-6 flex flex-col" data-testid="calc-result">
            <div className="text-sm font-bold uppercase tracking-wider text-white/50">Result</div>
            <div className="mt-3">
              <div className="font-heading text-4xl font-extrabold text-ai" data-testid="calc-headline">{result.headline}</div>
              <div className="text-sm text-white/60">{result.headlineLabel}</div>
            </div>
            <div className={`mt-4 inline-flex self-start rounded-full border px-3 py-1 text-xs font-bold ${VC[result.verdict.c]}`} data-testid="calc-verdict">{result.verdict.t}</div>
            <div className="mt-5 space-y-2 border-t border-white/10 pt-4">
              {result.rows.map(([k, val]) => (
                <div key={k} className="flex justify-between text-sm">
                  <span className="text-white/55">{k}</span>
                  <span className="font-mono text-white/90">{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm text-white/60">
          <span className="font-semibold text-white/80">Formula:</span> <span className="font-mono">{calc.formula}</span>
        </div>

        <div className="mt-6">{calc.explainer.map((b, i) => <ExplainerBlock key={i} b={b} />)}</div>

        <div className="mt-12 relative overflow-hidden rounded-3xl border border-ai/30 bg-gradient-to-br from-ai/20 to-transparent p-8 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,94,0,0.15),transparent_70%)]" />
          <div className="relative">
            <h3 className="font-heading text-2xl font-extrabold">Stop calculating one item at a time</h3>
            <p className="mt-2 text-white/70">AssortIQ computes this for every product and store automatically — and tells you what to do about it. Free for 7 days.</p>
            <button data-testid="calc-cta" onClick={onGetStarted} className="mt-5 inline-flex items-center gap-2 rounded-full bg-ai px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
              Start free trial <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <footer className="border-t border-white/10"><div className="mx-auto max-w-4xl px-6 py-8 text-xs text-white/40">© {new Date().getFullYear()} AssortIQ. Built for grocery, retail & agriculture.</div></footer>
    </div>
  );
}

function Index({ onHome, onGetStarted }) {
  useSEO({
    title: "Free Retail & Grain Calculators — GMROI, Turns, Weeks of Supply | AssortIQ",
    description: "Free calculators for retailers and farmers: GMROI, inventory turnover, weeks of supply and grain storage ROI. Instant results, no signup.",
    canonical: "https://getassortiq.com/tools",
  });
  return (
    <div className="min-h-screen bg-forest-900 text-white font-body antialiased">
      <Nav onHome={onHome} onGetStarted={onGetStarted} />
      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider"><Calculator className="h-3.5 w-3.5" /> Free tools</div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold">Free retail & grain calculators</h1>
          <p className="mt-3 text-white/60">Quick answers to the numbers that run your business — GMROI, turns, weeks of supply and grain storage ROI. Instant, in your browser, no signup.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tools-grid">
          {CALCS.map((c) => (
            <a key={c.slug} href={`/tools/${c.slug}`} data-testid={`tool-card-${c.slug}`} className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-ai/40 hover:-translate-y-1 transition-all">
              <div className="h-10 w-10 rounded-lg bg-ai/15 text-ai flex items-center justify-center"><Calculator className="h-5 w-5" /></div>
              <div className="mt-4 text-xs font-bold uppercase tracking-wider text-ai">{c.category}</div>
              <h2 className="mt-2 font-heading text-lg font-bold leading-snug group-hover:text-ai transition-colors">{c.h1}</h2>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{c.intro.slice(0, 120)}…</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-ai group-hover:gap-2.5 transition-all">Open calculator <ArrowRight className="h-4 w-4" /></span>
            </a>
          ))}
        </div>
      </div>
      <footer className="border-t border-white/10"><div className="mx-auto max-w-7xl px-6 py-8 text-xs text-white/40">© {new Date().getFullYear()} AssortIQ. Built for grocery, retail & agriculture.</div></footer>
    </div>
  );
}

export default function Calculators({ slug, onHome, onGetStarted }) {
  const calc = slug ? getCalc(slug) : null;
  if (slug && calc) return <CalcTool calc={calc} onHome={onHome} onGetStarted={onGetStarted} />;
  return <Index onHome={onHome} onGetStarted={onGetStarted} />;
}
